/**
 * Mise en page des textes d'un exercice V2 (PUR, testé).
 *
 * - `airSentences` : dans l'énoncé d'une question, une phrase par ligne — on va
 *   à la ligne après « . », « ? » ou « ! » suivis d'une majuscule. Jamais dans
 *   une formule (`$…$`, `$$…$$`, `\(…\)`, `\[…\]`), du code (`…`, blocs ```),
 *   un tableau ou une liste, ni après une abréviation (« M. », « cf. », « ex. »).
 * - `isTitleEcho` : le bloc « La situation » ne fait que répéter le titre de
 *   l'exercice (exercices de SKILLCARDS) → inutile de l'afficher.
 */
import type { ExerciseBlock } from './types';

const ABBREVIATIONS = new Set([
  'm',
  'mme',
  'mlle',
  'cf',
  'ex',
  'etc',
  'p',
  'pp',
  'env',
  'fig',
  'n°',
]);

/** Positions (début, fin) des passages à ne jamais couper : formules et code en ligne. */
function protectedRanges(line: string): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];
  const re = /\$\$[\s\S]*?\$\$|\$[^$\n]+\$|\\\([\s\S]*?\\\)|\\\[[\s\S]*?\\\]|`[^`\n]*`/g;
  for (let m = re.exec(line); m; m = re.exec(line)) ranges.push([m.index, m.index + m[0].length]);
  return ranges;
}

function airLine(line: string): string {
  const trimmed = line.trimStart();
  // Listes, tableaux, titres, citations : on ne touche pas.
  if (/^([-*+>|#]|\d+[.)]\s)/.test(trimmed)) return line;
  const ranges = protectedRanges(line);
  const inside = (i: number) => ranges.some(([a, b]) => i >= a && i < b);
  let out = '';
  let last = 0;
  // Fin de phrase : . ? ! (éventuellement suivis d'une parenthèse / guillemet
  // fermants), des espaces, puis une majuscule ou un guillemet ouvrant.
  const re = /([.?!][)»"]?)(\s+)(?=[A-ZÀ-ÖØ-Þ«])/g;
  for (let m = re.exec(line); m; m = re.exec(line)) {
    const at = m.index;
    if (inside(at)) continue;
    if (m[1]!.startsWith('.')) {
      const word = /([\p{L}°]+)$/u.exec(line.slice(0, at))?.[1]?.toLowerCase() ?? '';
      if (ABBREVIATIONS.has(word)) continue;
      // Initiale (« J. Dupont ») : une seule lettre majuscule avant le point.
      if (/(^|\s)\p{Lu}$/u.test(line.slice(0, at))) continue;
    }
    out += line.slice(last, at + m[1]!.length) + '\n\n';
    last = at + m[0].length;
  }
  return out + line.slice(last);
}

/** Une phrase par paragraphe dans l'énoncé d'une question (markdown). */
export function airSentences(md: string): string {
  if (!md) return md;
  const lines = md.split('\n');
  let inFence = false;
  return lines
    .map((line) => {
      if (/^\s*```/.test(line)) {
        inFence = !inFence;
        return line;
      }
      return inFence ? line : airLine(line);
    })
    .join('\n');
}

const normalize = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s*·\s*niveau\s*[123]\s*$/i, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .toLowerCase();

/** Le bloc d'énoncé ne contient que le titre de l'exercice (sans figure). */
export function isTitleEcho(block: ExerciseBlock | null, title: string): boolean {
  if (!block || block.block_type !== 'statement') return false;
  const data = block.data as { content_md?: string; markdown?: string; visuals?: unknown[] };
  if (Array.isArray(data.visuals) && data.visuals.length > 0) return false;
  const text = (data.content_md ?? data.markdown ?? '').trim();
  if (!text) return false;
  return normalize(text) === normalize(title);
}
